/**
 * WrapUpsellCard — the closing "That's a wrap!" card of the first-challenge tour.
 *
 * For a guest it adds a benefit-framed "Save my progress" CTA above the "Let's go"
 * continue-as-guest button (honest carrot: 1000 coins, add friends, full experience); a real
 * (signed-in) user sees only "Let's go". Presentational: the overlay owns the analytics +
 * navigation and passes the two handlers in.
 * See specs/guest-first-onboarding/spec.md (Story 5).
 */

import { View, Text, Pressable, StyleSheet } from 'react-native';
import { AnimatedMonkey } from '../../onboarding/AnimatedMonkey/AnimatedMonkey';
import { COACH_C } from '../CoachCard/CoachCard.constants';
import { FC_WRAP_GUEST_CTA } from '../../../src/onboarding/constants/firstChallengeTour.constants';

export interface WrapUpsellCardProps {
  /** Anonymous guest → show the sign-up CTA. Real user → only the continue button. */
  isGuest: boolean;
  title: string;
  text: string;
  /** Guest taps "Save my progress" → sign up. */
  onSignUp: () => void;
  /** "Let's go" → finish the tour as-is (also the guest's non-destructive dismissal). */
  onContinue: () => void;
}

export function WrapUpsellCard({ isGuest, title, text, onSignUp, onContinue }: WrapUpsellCardProps) {
  return (
    <View style={styles.wrapBackdrop} pointerEvents="auto">
      <View style={styles.wrapMascot}><AnimatedMonkey size={120} /></View>
      <Text style={styles.wrapTitle}>{title}</Text>
      <Text style={styles.wrapText}>{text}</Text>
      {isGuest ? (
        <>
          <Text style={styles.wrapBenefits}>{FC_WRAP_GUEST_CTA.benefits}</Text>
          <Pressable onPress={onSignUp} testID="fc-wrap-signup" style={({ pressed }) => [styles.wrapBtn, pressed && styles.pressed]}>
            <Text style={styles.wrapBtnText}>{FC_WRAP_GUEST_CTA.signUp}</Text>
          </Pressable>
          <Pressable onPress={onContinue} testID="fc-coach-done" style={({ pressed }) => [styles.wrapBtnGhost, pressed && styles.pressed]}>
            <Text style={styles.wrapBtnGhostText}>Let&apos;s go</Text>
          </Pressable>
        </>
      ) : (
        <Pressable onPress={onContinue} testID="fc-coach-done" style={({ pressed }) => [styles.wrapBtn, pressed && styles.pressed]}>
          <Text style={styles.wrapBtnText}>Let&apos;s go</Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrapBackdrop: {
    ...StyleSheet.absoluteFillObject, zIndex: 1001, elevation: 1001,
    backgroundColor: 'rgba(8,6,15,0.82)', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 32,
  },
  wrapMascot: { marginBottom: 8 },
  wrapTitle: { color: COACH_C.ink, fontSize: 28, fontWeight: '800', textAlign: 'center', letterSpacing: -0.4, marginTop: 6 },
  wrapText: { color: '#9CA3AF', fontSize: 15, lineHeight: 22, textAlign: 'center', marginTop: 8 },
  wrapBenefits: {
    color: COACH_C.ink, fontSize: 14, lineHeight: 20, textAlign: 'center',
    marginTop: 18, opacity: 0.9, maxWidth: 300,
  },
  wrapBtn: {
    marginTop: 28, minHeight: 52, maxWidth: 280, width: '100%', borderRadius: 12,
    backgroundColor: COACH_C.accent, alignItems: 'center', justifyContent: 'center',
  },
  wrapBtnText: { color: COACH_C.accentInk, fontSize: 16, fontWeight: '700' },
  wrapBtnGhost: {
    marginTop: 12, minHeight: 48, maxWidth: 280, width: '100%', borderRadius: 12,
    alignItems: 'center', justifyContent: 'center',
  },
  wrapBtnGhostText: { color: '#9CA3AF', fontSize: 15, fontWeight: '600' },
  pressed: { transform: [{ scale: 0.97 }] },
});
